import { Component, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';

import { TaxonomyDropdownComponent } from './taxonomy-dropdown/taxonomy-dropdown.component';
import { TaxonomyApiService } from '../../services/api-services/taxonomy-api.service';
import { TaxonomyService } from '../../services/taxonomy.service';
import { ActionService } from '../../services/action.service';
import { LocalTaxonomy } from './taxonomy.model';
import { TaxonomyCardComponent } from './taxonomy-card/taxonomy-card.component';
import { DropdownObjectType } from '../../shared/types';
import { TAXONOMY_FILTER_VALUES } from './taxonomy.constants';
import { Router } from '@angular/router';
import { TaxonomyCreateRequest } from './taxonomy.model';
import { NotificationService } from '../../services/notification.service';
import { TaxonomyConfigureComponent } from './taxonomy-configure/taxonomy-configure.component';

@Component({
  selector: 'taxonomy-dashboard-v2',
  standalone: true,
  imports: [TaxonomyDropdownComponent, CommonModule, TaxonomyCardComponent, TaxonomyConfigureComponent],
  templateUrl: './taxonomy-dashboard-v2.component.html',
  styleUrl: './taxonomy-dashboard-v2.component.scss',
})
export class TaxonomyDashboardV2Component {
  constructor(
    public actionsService: ActionService,
    private taxonomyService: TaxonomyService,
    private taxonomyApiService: TaxonomyApiService,
    public notificationService: NotificationService,
    private router: Router
  ) {}

  _taxonomiesCards = computed<LocalTaxonomy[]>(() => this.taxonomyService._taxonomiesData());
  _searchText = signal<string>('');
  _taxonomyName = signal<string>('');
  _selectedFilter = signal<DropdownObjectType>(TAXONOMY_FILTER_VALUES[0]);
  _showNamePopup = signal<boolean>(false);
  _existingTaxonomyNames = signal<string[]>([]);
  _isNameUnique = signal<boolean>(true);

  _taxonomyId = signal<string>('');
  isCreating = signal<boolean>(false);

  inputFocused = false;

  ngOnInit() {
    this.taxonomyService.loadTaxonomy();
    this.getTaxonomyCards();
    this.loadExistingTaxonomyNames();
  }

  loadExistingTaxonomyNames() {
    this.taxonomyApiService.getExistingTaxonomyNames({}).subscribe({
      next: (response) => {
        if (response?.success && response?.data) {
          this._existingTaxonomyNames.set(response.data);
        }
      },
      error: (err) => {
        console.error('Failed to fetch existing taxonomy names on init:', err);
      },
    });
  }

  onSearchChange(event: Event) {
    const el = event.target as HTMLInputElement;
    this._searchText.set(el.value);
  }

  onNameChange(event: Event) {
    const el = event.target as HTMLInputElement;
    const trimmedValue = el.value.trimStart();
    this._taxonomyName.set(trimmedValue);
    this.validateTaxonomyName(trimmedValue);
  }

  validateTaxonomyName(name: string): void {
    const trimmedName = name.trim();
    if (trimmedName.length === 0) {
      this._isNameUnique.set(true);
      return;
    }

    const existingNames = this._existingTaxonomyNames();
    const isUnique = !existingNames.some((existingName) => existingName.toLowerCase() === trimmedName.toLowerCase());
    this._isNameUnique.set(isUnique);
  }

  onTaxoBlur(): void {
    const currentValue = this._taxonomyName();
    this._taxonomyName.set(currentValue.trim());
    this.validateTaxonomyName(currentValue.trim());
    this.inputFocused = false;
  }

  handleFilterSelect(filter: DropdownObjectType) {
    this._selectedFilter.set(filter);
  }

  getTaxonomyCards = () => {
    let data = this._taxonomiesCards();
    const selectedFilterValue = this._selectedFilter().value;

    if (selectedFilterValue === 'published') {
      data = data.filter((i) => i.version_status === 'published');
    } else if (selectedFilterValue === 'draft') {
      data = data.filter((i) => i.version_status === 'draft');
    }

    if (this._searchText().trim()) {
      data = data.filter((i) => i.taxonomy_name.toLowerCase().includes(this._searchText().trim().toLowerCase()));
    }

    return data;
  };

  formatRouteName(name: string): string {
    return name.trim().replace(/\s+/g, '-').toLowerCase();
  }

  handleCreateTaxonomy() {
    if (this.isCreating()) return; // Prevent multiple submissions

    const taxonomyName = this._taxonomyName().trim();
    if (!this._isNameUnique()) {
      this.notificationService.error('Create Taxonomy', 'Taxonomy name already exists. Please choose a unique name.');
      return;
    }

    this.isCreating.set(true);
    this.taxonomyApiService.createTaxomonyV2({ taxonomy_name: taxonomyName } as TaxonomyCreateRequest).subscribe({
      next: (response) => {
        const taxonomyId = response?.data.taxonomy_id;
        if (taxonomyId) {
          // First, set the basic taxonomy data immediately to ensure it's in localStorage
          const basicTaxonomyData = {
            taxonomy_id: taxonomyId,
            taxonomy_name: taxonomyName,
            version_history_details: response?.data.version_history_details || [],
            selected_taxonomy_version: response?.data.version_history_details?.[0] || null,
          };

          // Set the basic data immediately to localStorage
          this.taxonomyService.setSelectedValue(basicTaxonomyData);

          // Clear the taxonomy name and close the popup immediately
          this._taxonomyName.set('');
          this._showNamePopup.set(false);
          this._isNameUnique.set(true);

          // Navigate to the taxonomy page
          this.taxonomyService.navigateToTaxonomyPage(taxonomyId);
          this.notificationService.success('Create Taxonomy', 'New Taxonomy Created');

          // Add a small delay to ensure navigation completes before fetching version history
          setTimeout(() => {
            // Then fetch complete version history data to get all required fields
            this.taxonomyApiService.getVersionHistoryById({ taxonomy_id: taxonomyId }).subscribe({
              next: (versionHistoryResponse) => {
                const version_history_details = versionHistoryResponse?.data.version_history_details || [];
                const selected_taxonomy_version = version_history_details[0];

                // Update with complete data
                const completeTaxonomyData = {
                  taxonomy_id: taxonomyId,
                  taxonomy_name: basicTaxonomyData.taxonomy_name, // Use the stored name
                  version_history_details: version_history_details,
                  selected_taxonomy_version: selected_taxonomy_version,
                };

                this.taxonomyService.setSelectedValue(completeTaxonomyData);

                // Refresh the taxonomy list to include the new taxonomy
                this.taxonomyService.loadTaxonomy();
              },
              error: (versionHistoryErr) => {
                console.error('Failed to fetch version history after taxonomy creation:', versionHistoryErr);
                // The basic data is already set, so we don't need to do anything else

                // Still refresh the taxonomy list even if version history fetch fails
                this.taxonomyService.loadTaxonomy();
              },
            });
          }, 100); // Small delay to ensure navigation completes
        } else {
          console.error('Taxonomy ID missing in response');
        }
      },
      error: (err) => {
        console.error('---', err);
        this.notificationService.error('Create Taxonomy', 'Failed to create taxonomy');
      },
      complete: () => {
        this.isCreating.set(false);
      },
    });
  }

  handleEmptyStateCreate() {
    const taxonomyName = this._taxonomyName().trim();
    if (taxonomyName.length === 0) {
      return;
    }

    // If we don't have existing names yet, fetch them first
    if (this._existingTaxonomyNames().length === 0) {
      this.taxonomyApiService.getExistingTaxonomyNames({}).subscribe({
        next: (response) => {
          if (response?.success && response?.data) {
            this._existingTaxonomyNames.set(response.data);
            this.validateTaxonomyName(taxonomyName);

            // If name is unique, proceed with creation
            if (this._isNameUnique()) {
              this.handleCreateTaxonomy();
            } else {
              this.notificationService.error(
                'Create Taxonomy',
                'Taxonomy name already exists. Please choose a unique name.'
              );
            }
          }
        },
        error: (err) => {
          console.error('Failed to fetch existing taxonomy names:', err);
          this.notificationService.error('Error', 'Failed to fetch existing taxonomy names');
        },
      });
    } else {
      // We already have existing names, proceed with validation and creation
      this.handleCreateTaxonomy();
    }
  }

  handleOpenNamePopup() {
    if (!this._showNamePopup()) {
      // Fetch existing taxonomy names when opening the popup
      this.taxonomyApiService.getExistingTaxonomyNames({}).subscribe({
        next: (response) => {
          if (response?.success && response?.data) {
            this._existingTaxonomyNames.set(response.data);
          }
          this._showNamePopup.set(true);
        },
        error: (err) => {
          console.error('Failed to fetch existing taxonomy names:', err);
          this.notificationService.error('Error', 'Failed to fetch existing taxonomy names');
          // Still show the popup even if the API call fails
          this._showNamePopup.set(true);
        },
      });
    } else {
      this._showNamePopup.set(false);
      this._taxonomyName.set('');
      this._isNameUnique.set(true);
    }
  }

  handleViewTaxonomy(
    taxonomyId: string,
    taxonomyName: string,
    version_history_details: any,
    selected_taxonomy_version: any
  ) {
    const selectedValue = {
      taxonomy_id: taxonomyId,
      taxonomy_name: taxonomyName,

      version_history_details: version_history_details,
      selected_taxonomy_version: selected_taxonomy_version,
    };
    this.taxonomyService.setSelectedValue(selectedValue);
  }
}
